import type { MaybeComputedElementRef } from '@vueuse/core';
import type { ComputedRef, MaybeRefOrGetter } from 'vue';
import { useElementSize } from '@vueuse/core';
import { computed, toValue } from 'vue';

type ContainerBreakpoints<K extends string> = Record<K, MaybeRefOrGetter<number>>;

export interface UseContainerQueryReturn<K extends string> {
	greaterOrEqual: (breakpoint: MaybeRefOrGetter<K>) => ComputedRef<boolean>;
	smallerOrEqual: (breakpoint: MaybeRefOrGetter<K>) => ComputedRef<boolean>;
	greater: (breakpoint: MaybeRefOrGetter<K>) => ComputedRef<boolean>;
	smaller: (breakpoint: MaybeRefOrGetter<K>) => ComputedRef<boolean>;
	between: (lower: MaybeRefOrGetter<K>, upper: MaybeRefOrGetter<K>) => ComputedRef<boolean>;
	current: () => ComputedRef<K[]>;
	active: () => ComputedRef<K | ''>;
}

export function useContainerQuery<K extends string>(
	target: MaybeComputedElementRef,
	breakpoints: ContainerBreakpoints<K>,
): UseContainerQueryReturn<K> {
	const { width } = useElementSize(target, { width: Number.NaN, height: 0 });
	const breakpointNames = Object.keys(breakpoints) as K[];

	function compare(breakpoint: MaybeRefOrGetter<K>, predicate: (width: number, breakpoint: number) => boolean) {
		return computed(() => {
			if (Number.isNaN(width.value)) return false;

			return predicate(width.value, toValue(breakpoints[toValue(breakpoint)]));
		});
	}

	function greaterOrEqual(breakpoint: MaybeRefOrGetter<K>) {
		return compare(breakpoint, (containerWidth, breakpointWidth) => containerWidth >= breakpointWidth);
	}

	function smallerOrEqual(breakpoint: MaybeRefOrGetter<K>) {
		return compare(breakpoint, (containerWidth, breakpointWidth) => containerWidth <= breakpointWidth);
	}

	function greater(breakpoint: MaybeRefOrGetter<K>) {
		return compare(breakpoint, (containerWidth, breakpointWidth) => containerWidth > breakpointWidth);
	}

	function smaller(breakpoint: MaybeRefOrGetter<K>) {
		return compare(breakpoint, (containerWidth, breakpointWidth) => containerWidth < breakpointWidth);
	}

	function between(lower: MaybeRefOrGetter<K>, upper: MaybeRefOrGetter<K>) {
		return computed(() => {
			if (Number.isNaN(width.value)) return false;

			return width.value >= toValue(breakpoints[toValue(lower)])
				&& width.value < toValue(breakpoints[toValue(upper)]);
		});
	}

	function current() {
		return computed(() => {
			if (Number.isNaN(width.value)) return [];
			const containerWidth = width.value;

			return breakpointNames
				.filter((breakpoint) => containerWidth >= toValue(breakpoints[breakpoint]))
				.toSorted((left, right) => toValue(breakpoints[left]) - toValue(breakpoints[right]));
		});
	}

	function active() {
		const currentBreakpoints = current();

		return computed(() => currentBreakpoints.value.at(-1) ?? '');
	}

	return {
		greaterOrEqual,
		smallerOrEqual,
		greater,
		smaller,
		between,
		current,
		active,
	};
}
